import React, { useState } from "react";
import { toast } from "sonner";
import { FolderSearch, Eye, Loader2, CheckCircle2 } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";
import { Md } from "@/components/academy/Md";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

function Scorer({ c, rubric, onSaved }) {
  const [scores, setScores] = useState(c.result?.scores || rubric.map(() => 0));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await academy.post(`/cases/${c.id}/score`, { scores });
      toast.success("Score saved");
      onSaved();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 space-y-3" data-testid={`case-rubric-${c.id}`}>
      <div className="text-[12px] uppercase tracking-wider text-zinc-500">Model answer and rubric · score yourself honestly</div>
      {rubric.map((r, i) => (
        <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-zinc-100">{r.label} <span className="text-zinc-500">({r.points} pts)</span></span>
            <div className="flex gap-1">
              {Array.from({ length: r.points + 1 }, (_, p) => (
                <button key={p} data-testid={`case-${c.id}-score-${i}-${p}`} onClick={() => setScores((s) => s.map((v, j) => (j === i ? p : v)))}
                  className={`h-8 w-8 rounded-md border text-[13px] font-semibold transition-colors ${scores[i] === p ? "border-blue-500 bg-blue-600 text-white" : "border-zinc-700 text-zinc-400 hover:border-blue-500/60"}`}>{p}</button>
              ))}
            </div>
          </div>
          <p className="mt-2 text-[14px] leading-relaxed text-zinc-300">{r.model}</p>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <span className="text-[14px] text-zinc-300">Total: <b data-testid={`case-${c.id}-total`}>{scores.reduce((a, b) => a + b, 0)}</b> / {c.max_points}</span>
        <button data-testid={`case-${c.id}-save-score`} onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Save my score
        </button>
      </div>
    </div>
  );
}

function CaseFile({ c, n, onChange }) {
  const [answers, setAnswers] = useState(c.result?.answers || c.tasks.map(() => ""));
  const [saveAns, setSaveAns] = useState(false);
  const [rubric, setRubric] = useState(c.rubric);
  const [busy, setBusy] = useState(false);
  const reveal = async () => {
    setBusy(true);
    try {
      const r = await academy.post(`/cases/${c.id}/reveal`, { answers, save_answers: saveAns });
      setRubric(r.data.rubric);
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const scored = c.result && c.result.points != null;
  return (
    <article className="rounded-2xl border border-zinc-800/70 bg-[#111113] p-6" data-testid={`case-${c.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-blue-600/15 text-blue-400"><FolderSearch size={18} /></span>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Case File {n} · {c.platform}</div>
          <h3 className="text-lg font-semibold text-zinc-50">{c.title}</h3>
        </div>
        {scored && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-[12px] font-semibold text-emerald-300"><CheckCircle2 size={13} /> {c.result.points}/{c.max_points}</span>}
      </div>
      <Md className="mt-3">{c.story}</Md>
      <div className="mt-4 space-y-3">
        {c.tasks.map((t, i) => (
          <div key={i}>
            <label className="text-[14px] font-medium text-zinc-200">{i + 1}. {t}</label>
            <Textarea data-testid={`case-${c.id}-answer-${i}`} value={answers[i] || ""} disabled={!!rubric}
              onChange={(e) => setAnswers((a) => a.map((v, j) => (j === i ? e.target.value : v)))}
              className="mt-1.5 min-h-[70px] border-zinc-800 bg-zinc-950 text-[14px] text-zinc-100" placeholder="Your answer…" />
          </div>
        ))}
      </div>
      {!rubric ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-[13px] text-zinc-400">
            <Checkbox data-testid={`case-${c.id}-save-answers`} checked={saveAns} onCheckedChange={(v) => setSaveAns(!!v)} className="border-zinc-700 data-[state=checked]:bg-blue-600" />
            Save my typed answers (optional)
          </label>
          <button data-testid={`case-${c.id}-reveal`} onClick={reveal} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Reveal model answer
          </button>
        </div>
      ) : <Scorer c={c} rubric={rubric} onSaved={onChange} />}
    </article>
  );
}

export function CaseFiles({ mod, reload }) {
  const s = mod.summary;
  return (
    <div className="space-y-5" data-testid="academy-capstone">
      <div className="rounded-xl border border-zinc-800/70 bg-[#0b1430] p-5">
        <Md>{mod.intro}</Md>
        <div className="mt-2 text-[14px] text-zinc-200" data-testid="capstone-points">Your points: <b>{s.points}</b> / 50 · {s.cases_done}/{s.cases_total} cases scored · pass mark 35</div>
      </div>
      {mod.cases.map((c, i) => <CaseFile key={c.id} c={c} n={i + 1} onChange={reload} />)}
    </div>
  );
}
